import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { baseUrl, userLocation, ipAddress, userName } from "../Univ.js";

function ProductionEntryPage() {
  const [rows, setRows] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);

  const [data, setData] = useState({
    production_id: "",
    machine_id: "",
    operator_id: "",
    produced_qty: "",
    rejected_qty: "0"
  });

  useEffect(() => {
    fetchProductionEntries();
    fetchProductionOrders();
    fetchMachines();
    fetchOperators();
  }, []);

  // Fetch Production Entries
  const fetchProductionEntries = () => {
    axios.get(baseUrl + "production-entry/")
      .then(res => setRows(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Production Entries", "error"));
  };

  const fetchProductionOrders = () => {
    axios.get(baseUrl + "production-orders/")
      .then(res => setProductionOrders(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Production Orders", "error"));
  };

  const fetchMachines = () => {
    axios.get(baseUrl + "machines/")
      .then(res => setMachines(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Machines", "error"));
  };

  const fetchOperators = () => {
    axios.get(baseUrl + "add-employee/")
      .then(res => setOperators(res.data))
      .catch(() => Swal.fire("Error", "Could not fetch Operators", "error"));
  };

  const inputHandler = (e) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  // Submit new Production Entry
  const formSubmit = () => {
    if (!data.production_id || !data.machine_id || !data.operator_id || !data.produced_qty) {
      Swal.fire("Error", "All fields except Rejected Qty are required", "error");
      return;
    }

    const _formData = new FormData();
    _formData.append("production", data.production_id);
    _formData.append("machine", data.machine_id);
    _formData.append("operator", data.operator_id);
    _formData.append("produced_qty", data.produced_qty);
    _formData.append("rejected_qty", data.rejected_qty || 0);
    _formData.append("status", "1");
    _formData.append("addedby", userName);
    _formData.append("ip_add", ipAddress);
    _formData.append("location", userLocation);

    axios.post(baseUrl + "production-entry/", _formData, {
      headers: { "Content-Type": "multipart/form-data" }
    })
    .then(response => {
      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          title: "Production Entry Created",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
        setData({
          production_id: "",
          machine_id: "",
          operator_id: "",
          produced_qty: "",
          rejected_qty: "0"
        });
        fetchProductionEntries();
      }
    })
    .catch(() => Swal.fire("Error", "Something went wrong", "error"));
  };

  // Delete
  const handleDelete = (id) => {
    Swal.fire({
      title: "Confirm",
      text: "Are you sure you want to delete this?",
      icon: "warning",
      confirmButtonText: "Yes",
      showCancelButton: true
    }).then(result => {
      if (result.isConfirmed) {
        axios.delete(baseUrl + "production-entry/" + id + "/")
          .then(() => {
            Swal.fire("Deleted", "Production Entry deleted", "success");
            fetchProductionEntries();
          });
      }
    });
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    {
      field: "production",
      headerName: "Production Order",
      flex: 1,
      renderCell: (params) => params.row.production?.order_no || "Unknown"
    },
    {
      field: "machine",
      headerName: "Machine",
      flex: 1,
      renderCell: (params) => params.row.machine?.name || "Unknown"
    },
    {
      field: "operator",
      headerName: "Operator",
      flex: 1,
      renderCell: (params) => params.row.operator?.employee || "Unknown"
    },
    { field: "produced_qty", headerName: "Produced Qty", flex: 1 },
    { field: "rejected_qty", headerName: "Rejected Qty", flex: 1 },
    {
      field: "edit",
      headerName: "Edit",
      renderCell: (params) => (
        <Link
          to={`/production-entry/edit/${params.row.id}`}
          target="_blank"
          className="btn btn-warning btn-sm"
        >
          Edit
        </Link>
      ),
      width: 120
    },
    {
      field: "delete",
      headerName: "Delete",
      renderCell: (params) => (
        <button
          onClick={() => handleDelete(params.row.id)}
          className="btn btn-danger btn-sm"
        >
          Delete
        </button>
      ),
      width: 120
    }
  ];

  return (
    <div className="container mt-4">

      {/* Form */}
      <div className="card p-4 mb-4">
        <h4>Add Production Entry</h4>

        <select
          className="form-control mt-3"
          name="production_id"
          value={data.production_id}
          onChange={inputHandler}
        >
          <option value="">Select Production Order</option>
          {productionOrders.map(po => (
            <option key={po.id} value={po.id}>
              {po.order_no} - {po.product?.name}
            </option>
          ))}
        </select>

        <select
          className="form-control mt-3"
          name="machine_id"
          value={data.machine_id}
          onChange={inputHandler}
        >
          <option value="">Select Machine</option>
          {machines.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </select>

        <select
          className="form-control mt-3"
          name="operator_id"
          value={data.operator_id}
          onChange={inputHandler}
        >
          <option value="">Select Operator</option>
          {operators.map(o => (
            <option key={o.id} value={o.id}>{o.employee}</option>
          ))}
        </select>

        <input
          type="number"
          className="form-control mt-3"
          placeholder="Produced Quantity"
          name="produced_qty"
          value={data.produced_qty}
          onChange={inputHandler}
        />

        <input
          type="number"
          className="form-control mt-3"
          placeholder="Rejected Quantity (optional)"
          name="rejected_qty"
          value={data.rejected_qty}
          onChange={inputHandler}
        />

        <button className="btn btn-success mt-3" onClick={formSubmit}>
          Submit
        </button>
      </div>

      {/* DataGrid */}
      <div className="card p-3">
        <DataGrid
          sx={{
            boxShadow: 4,
            border: 4,
            borderColor: "primary.light",
            "& .MuiDataGrid-cell:hover": { color: "primary.main" }
          }}
          rows={rows}
          columns={columns}
          pageSize={10}
          autoHeight
          components={{ Toolbar: GridToolbar }}
        />
      </div>

    </div>
  );
}

export default ProductionEntryPage;
